import React, { useEffect, useState } from 'react';
import './ReportDetailOrg.scss';
import { Table, Row as AntRow, Col as AntCol } from 'antd';
import {
  Button,
  CustomPagination,
  Layout,
  notification,
  NotificationType,
  Picker,
  SearchInput,
  Spinner,
  Tooltip,
  TreeSelect,
  TreeSelectNodeType,
  Checkbox
} from '@iba-team/portal-react-components/dist';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DownloadIcon } from '@iba-team/portal-react-components/dist/svg/outline/download.svg';
import { ReactComponent as SearchIcon } from '@iba-team/portal-react-components/dist/svg/outline/search.svg';
import { formatDate } from '../../utils/formatDateAndTime';
import { getDate, getMonth, getYear, format } from 'date-fns';
import { buildDownloadFile, shortenStr, sendUsageRequest, sendUsageRequestWithBody } from '../../utils/functions';
import { ReportDetailsOrgStore } from '../../store/ReportDetailsOrg';
import { observer } from 'mobx-react';
import { BackArrow } from './BackArrow';
import { Link } from 'react-router-dom';
import { useEffectOnce, useLocalStorage, useSessionStorage, useSetState, useUpdateEffect } from 'react-use';
import { CountryListStore } from '../../store/CountryList';
import { PROFILE_ROUTE, REPORTS_DETAIL_ORG_ROUTE } from '../../utils/consts';
import { useLocation } from 'react-router';
import { useAnalyticsEventTracker } from '../../analytics/analytics';
import { useUsageStatistics } from '../../utils/hooks';
import { ArrowButtons } from '../../utils_components/ArrowButtons';
import { HrReportEmployeeDto, OrgReportDivisionItem, OrgReportSettingsUpdateDto } from '../../types';
import { ColumnsType } from 'antd/es/table';
import LeaveTypes from '../LeaveTypes';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import Sorter from '../../utils_components/Sorter';
import apiConfig from '../../api/routes';
import InvalidReportAlert from '../banner/InvalidReportAlert';

export type Col = {
  key: number,
  value: string,
  tooltip: string | null,
  sortable: boolean
}

export type TableDataT = {
  result: {id: number, columns: any[], userActive: boolean, profileLinkVisible: boolean}[],
  total: number
}

export interface ReportOrgFilters{
  dateFilter: string,
  page: number | undefined,
  perPage: number,
  query: string,
  sorting: {
    name: string,
    order: string
  }
}

const ReportDetailOrg = observer(() => {
  const { t } = useTranslation();
  const {allLeaveTypes} = LeaveTypesStore;
  const {
    downloadTimesheetData,
    downloadTimesheetDataAll,
    fetchingData,
    getConfiguration,
    hrReportData,
    configuration,
    updateConfiguration,
    fetchNewTableData} = ReportDetailsOrgStore;
  const todayDate = new Date();
  const [currentDate, setCurrentDate] = useState<Date>(todayDate);
  const [nameToApi, setNameToApi] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [searchLine, setSearchLine] = useState('');
  const [dateToApi, setDateToApi] = useState(formatDate(todayDate));
  const [showPerPage, setShowPerPage] = useState(15);
  const [activePage, setActivePage] = useSessionStorage('activePage', 1);
  const [isDisabled, setIsDisabled] = useState(false);
  const { getUserLocation } = CountryListStore;
  const [perPageLocalStorage, setPerPageLocalStorage] = useLocalStorage('perPage_Org', 15);
  const currentRoute = useLocation();
  const gaEventTracker = useAnalyticsEventTracker('Report');

  const [upgradeFilters, setUpgradeFilters] = useSetState<OrgReportSettingsUpdateDto>({
    divisionIds: [],
    isAllDivisionsSelected: false,
    isAllLocationsSelected: false,
    locationIds: [],
    includeB2B: false,
    includeOnlyPendingAbsence: false
  });

  const [divisionTagPlaceholder, setDivisionTagPlaceholder] = useState('');
  const [locationTagPlaceholder, setLocationTagPlaceholder] = useState('');
  const [isChangedManually, setIsChangedManually] = useState(false);
  //Не удалять, там есть одна строка
  const withoutAllKey = upgradeFilters.locationIds.filter(i => typeof i !== 'string');
  const toApi: ReportOrgFilters = {
    dateFilter: dateToApi,
    page: activePage,
    perPage: showPerPage,
    query: searchLine,
    sorting: {
      name: nameToApi,
      order: sortOrder
    }
  };

  useUsageStatistics(apiConfig.statisticsUsageService.hrReportPage);

  useUpdateEffect(() => {
    fetchNewTableData({
      ...toApi,
      divisionsAndLocationsFilter: {...upgradeFilters, locationIds: withoutAllKey}
    });
  }, [upgradeFilters, nameToApi, sortOrder, searchLine, dateToApi, activePage, showPerPage,]);

  useEffect(() => {
    if(upgradeFilters.locationIds.length === 1){
      const findById = configuration.locations.find(i=> i.id === upgradeFilters.locationIds[0])?.name || '';
      setLocationTagPlaceholder(findById);
    } else if (upgradeFilters.isAllLocationsSelected) {
      setLocationTagPlaceholder('All locations');
    } else if (upgradeFilters.locationIds.length > 1){
      setLocationTagPlaceholder(`Selected: ${upgradeFilters.locationIds.length}`);
    }
  }, [configuration.locations, upgradeFilters.locationIds, upgradeFilters.isAllLocationsSelected]);
  const parseSelectedDivisions = (array: OrgReportDivisionItem[]) => {
    for (const item of array) {
      if(item.selected){
        setUpgradeFilters(prev => ({divisionIds: [...prev.divisionIds, item.id]}));
      }
      if (!item.selected && item.children?.length) {
        parseSelectedDivisions(item.children);
      }
    }
  };

  useUpdateEffect(() => {
    parseSelectedDivisions(configuration.divisionTreeForHrReportFilterDtos);
    if(configuration.allLocationsSelected){
      setUpgradeFilters({locationIds: ['ALL',...configuration.locations.map(i => i.id)]});
    }
    else{
      setUpgradeFilters({locationIds: configuration.locations.filter(i => i.selected).map(i => i.id)});
    }
    setUpgradeFilters({
      isAllLocationsSelected: configuration.allLocationsSelected,
      isAllDivisionsSelected: configuration.allDivisionsSelected,
      includeB2B: configuration.includeB2B,
      includeOnlyPendingAbsence: configuration.includeOnlyPendingAbsence
    });
  }, [configuration]);

  useEffectOnce(()=> {
    if(perPageLocalStorage){
      setShowPerPage(perPageLocalStorage);
    }
  });

  useEffect(() => {
    getConfiguration();
  }, []);

  useEffect(() => {
    getUserLocation();
  },[]);

  const handleSort = (value: string) => {
    if (nameToApi === value) {
      setSortOrder( sortOrder === '' ? 'ASC' : sortOrder === 'ASC' ? 'DESC' : '' );
    } else {
      setNameToApi(value);
      setSortOrder('ASC');
    }
  };

  const prevMonth = () => {
    setCurrentDate(new Date(getYear(currentDate), getMonth(currentDate) - 1, getDate(currentDate)));
    setDateToApi(`${formatDate(currentDate)}`);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(getYear(currentDate), getMonth(currentDate) + 1, getDate(currentDate)));
    setDateToApi(`${formatDate(currentDate)}`);
  };

  const downloadAllData = () => {
    gaEventTracker('download_all_templates');
    setIsDisabled(true);
    notification.open({
      type: NotificationType.INFO,
      description: t('MSG0016')
    });
    const payload = {
      dateFilter: dateToApi,
      divisionsAndLocationsFilter: {...upgradeFilters, locationIds: upgradeFilters.locationIds.filter(i => i !== 'ALL')}
    };
    const dateForFileName = format(new Date(dateToApi), 'yyyy-MM-dd');
    downloadTimesheetDataAll(payload).then((response) => {
      return response.blob();
    }).then((blob) => buildDownloadFile(blob, `HR_report_${dateForFileName}.zip`)).then(() => setIsDisabled(false));
  };

  const downloadForUser =  (id:number, fullName: string) => {
    setIsDisabled(true);
    notification.open({
      type: NotificationType.INFO,
      description: t('MSG0016')
    });
    const dateForFileName = format(new Date(dateToApi), 'yyyy-MM-dd');
    downloadTimesheetData(dateToApi, [id]).then((response) => {
      return response.blob();
    }).then((blob) => buildDownloadFile(blob, `HR_report_${fullName}_${dateForFileName}.zip`)).then(() => setIsDisabled(false));
  };

  const pullLeaveTypeFromList = (list:  {leaveCategory: string, leaveCount: number}[] , sourceType: string) => {
    return list.find(i => i.leaveCategory === sourceType);
  };

  const handleProfileClick = (profileId: number) => {
    currentRoute.state = { prevUrl: REPORTS_DETAIL_ORG_ROUTE };
    sendUsageRequest(`${apiConfig.statisticsUsageService.hrReportGoToProfile}?profileId=${profileId}`);
  };

  const columns: ColumnsType<HrReportEmployeeDto> = [
    {
      title: '#',
      key: 'key',
      render: (_, data) => {
        return data.orderNumber;
      },
      width: 50,
    }, 
    {
      title: <>
        {(nameToApi === 'Employee') ?
          <Sorter text='Employee' sortOrder={sortOrder === 'ASC' ? 1 : sortOrder === 'DESC' ? -1 : 0 } />
          :
          <Sorter text='Employee' sortOrder={0} />
        }  
      </>,
      key: 'employee',
      onHeaderCell: (_) => {
        return {
          onClick: () => handleSort('Employee')
        };
      },
      render: (_, data) => {
        if(data.profileLinkVisible && !data.userActive){
          return (
            <Link
              onClick={() => {
                currentRoute.state = { prevUrl: REPORTS_DETAIL_ORG_ROUTE };
              }}
              to={`${PROFILE_ROUTE}/${data.id}`}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >{data.fullName} [Inactive]</Link>
          );
        }
        if(!data.profileLinkVisible && !data.userActive){
          return <div>{data.fullName} [Inactive]</div>;
        }
        if(data.profileLinkVisible && data.userActive){
          return (
            <div className='employee_wrap'>
              <Link
                onClick={() => {
                  handleProfileClick(data.id);
                }}
                onAuxClick={() => {
                  handleProfileClick(data.id);
                }}
                to={`${PROFILE_ROUTE}/${data.id}`}
                target={'_blank'}
                rel={'noopener noreferrer'}
              >{data.fullName}</Link>
              <Tooltip title={data.countryCodeToolTip}><div>{data.countryCode}</div></Tooltip>
            </div>
          );
        }
      },
      width: 288,
    },
    {
      title: <>
        {(nameToApi === 'Available') ?
          <Sorter text='Available' sortOrder={sortOrder === 'ASC' ? 1 : sortOrder === 'DESC' ? -1 : 0 } />
          :
          <Sorter text='Available' sortOrder={0} />
        }  
      </>,
      key: 'available',
      onHeaderCell: (_) => {
        return {
          onClick: () => handleSort('Available')
        };
      },
      render: (_, data) => {
        return `${data.availableDays} / ${data.allowanceDays}`;
      },
      width: 70,
    },
    {
      title: <Tooltip title="Vacation">
        <div className='icon_title'>
          <LeaveTypes
            leaveTypes={allLeaveTypes}
            withoutText={true}
            type='VACATION'
            infoMessageVisible={false}/>
        </div>
      </Tooltip>,
      key: 'vacation',
      render: (_, data) => {
        const type = pullLeaveTypeFromList(data.totalAbsence, 'VACATION')!;
        return type.leaveCount;
      },
      width: 70,
    },
    {
      title: <Tooltip title="Sick Leave">
        <div className='icon_title'>
          <LeaveTypes
            leaveTypes={allLeaveTypes}
            withoutText={true}
            type='SICK_LEAVE'
            infoMessageVisible={false}/>
        </div>
      </Tooltip>,
      key: 'sickleave',
      render: (_, data) => {
        const type = pullLeaveTypeFromList(data.totalAbsence, 'SICK_LEAVE')!;
        return type.leaveCount;
      },
      width: 70,
    },
    {
      title: <Tooltip title="Occasional Leave">
        <div className='icon_title'>
          <LeaveTypes
            leaveTypes={allLeaveTypes}
            withoutText={true}
            type='OCCASIONAL_LEAVE'
            infoMessageVisible={false}/>
        </div>
      </Tooltip>,
      key: 'occleave',
      render: (_, data) => {
        const type = pullLeaveTypeFromList(data.totalAbsence, 'OCCASIONAL_LEAVE')!;
        return type.leaveCount;
      },
      width: 70,
    },
    {
      title: <Tooltip title="Compensatory Off">
        <div className='icon_title'>
          <LeaveTypes
            leaveTypes={allLeaveTypes}
            withoutText={true}
            type='COMPENSATORY_OFF'
            infoMessageVisible={false}/>
        </div>
      </Tooltip>,
      key: 'compoff',
      render: (_, data) => {
        const type = pullLeaveTypeFromList(data.totalAbsence, 'COMPENSATORY_OFF')!;
        return type.leaveCount;
      },
      width: 70,
    },
    {
      title: <Tooltip title="Unpaid Leave">
        <div className='icon_title'>
          <LeaveTypes
            leaveTypes={allLeaveTypes}
            withoutText={true}
            type='UNPAID_LEAVE'
            infoMessageVisible={false}/>
        </div>
      </Tooltip>,
      key: 'unpaid',
      render: (_, data) => {
        const type = pullLeaveTypeFromList(data.totalAbsence, 'UNPAID_LEAVE')!;
        return type.leaveCount;
      },
      width: 70,
    },
    {
      title: <Tooltip title="Parental Leave">
        <div className='icon_title'>
          <LeaveTypes
            leaveTypes={allLeaveTypes}
            withoutText={true}
            type='PARENTAL_LEAVE'
            infoMessageVisible={false}/>
        </div>
      </Tooltip>,
      key: 'parental',
      render: (_, data) => {
        const type = pullLeaveTypeFromList(data.totalAbsence, 'PARENTAL_LEAVE')!;
        return type.leaveCount;
      },
      width: 70,
    },
    {
      title: <>
        {(nameToApi === 'Total') ?
          <Sorter text='Total' sortOrder={sortOrder === 'ASC' ? 1 : sortOrder === 'DESC' ? -1 : 0 } />
          :
          <Sorter text='Total' sortOrder={0} />
        }  
      </>,
      key: 'total',
      onHeaderCell: (_) => {
        return {
          onClick: () => handleSort('Total')
        };
      },
      render: (_, data) => {
        return data.totalUsed;
      },
      width: 70,
    },
    {
      title: '',
      key: 'download',
      render: (_, data) => {
        return  <Tooltip title={'Download'} >
          <div onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}>
            <DownloadIcon className={`${isDisabled ? 'download-icon-disabled' : 'download-icon'} download-icon-base`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                downloadForUser(data.id, data.fullName);
              }} />
          </div>
        </Tooltip>;
      },
      width: 50,
    }
  ];


  const columnsWithNessesaryType = () => {
    const res = columns;
    const index = res.length - 2;
    if(hrReportData.categories.findIndex(i => i.type === 'MILITARY_LEAVE') !== -1){
      res.splice(index, 0, {
        title: <Tooltip title="Military Leave">
          <div className='icon_title'>
            <LeaveTypes
              leaveTypes={allLeaveTypes}
              withoutText={true}
              type='MILITARY_LEAVE'
              infoMessageVisible={false}/>
          </div>
        </Tooltip>,
        key: 'military',
        render: (_, data) => {
          const type = pullLeaveTypeFromList(data.totalAbsence, 'MILITARY_LEAVE')!;
          return type.leaveCount;
        },
        width: 70,
      },);
    }
    return res;
  };

  useEffect(() => {
    currentDate && setDateToApi(`${formatDate(currentDate)}`);
  }, [currentDate]);


  const checkIfAllDivisionsSelected = (selectedIds : number[]) => {
    if (!configuration.divisionTreeForHrReportFilterDtos.length
        || configuration.divisionTreeForHrReportFilterDtos.length !== selectedIds.length) {
      return false;
    }
    const highLevelIds = configuration.divisionTreeForHrReportFilterDtos.map(el => el.id).sort();
    return selectedIds.sort().every((el, index) => el === highLevelIds[index]);
  };

  const getNameById = (array: OrgReportDivisionItem[], id: number): string => {
    for (const item of array) {
      if (item.id === id) return item.name;
      if (item.children?.length) {
        const innerResult = getNameById(item.children, id);
        if (innerResult) return innerResult;
      }
    }
    return '';
  };

  useEffect(() => {
    if(isChangedManually){
      updateConfiguration({...upgradeFilters, locationIds: withoutAllKey, isAllLocationsSelected: withoutAllKey.length === configuration.locations.length});
    }
  }, [isChangedManually, upgradeFilters, configuration]);

  useEffect(() => {
    if (upgradeFilters.divisionIds.length === 1) {
      setDivisionTagPlaceholder(getNameById(configuration.divisionTreeForHrReportFilterDtos, upgradeFilters.divisionIds[0]));
    } else if (upgradeFilters.isAllDivisionsSelected) {
      setDivisionTagPlaceholder('All divisions');
    } else if (upgradeFilters.divisionIds.length > 1) {
      setDivisionTagPlaceholder(`Selected: ${upgradeFilters.divisionIds.length}`);
    }
  }, [upgradeFilters.divisionIds, upgradeFilters.isAllDivisionsSelected, divisionTagPlaceholder]);

  const onSelectDivision = (ids: number[]) => {
    setActivePage(1);
    const isAllSelected = checkIfAllDivisionsSelected(ids);
    setUpgradeFilters({divisionIds: ids, isAllDivisionsSelected: isAllSelected});
    sendUsageRequestWithBody(`${apiConfig.statisticsUsageService.hrReportFilterDivisions}`, ids);
    setIsChangedManually(true);
  };

  const onSelectLocation = (ids: number[]) => {
    let idsToSend: number [] = [];
    let isAllSelectedToSend = false;
    setActivePage(1);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (ids.includes('ALL')) {
      if (upgradeFilters.locationIds.includes('ALL')) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        idsToSend = ids.filter(i => i !== 'ALL');
        isAllSelectedToSend = false;
      } else {
        idsToSend = [...configuration.locations.map(l => l.id), 'ALL'] as unknown as number[];
        isAllSelectedToSend = true;
      }
    }
    else if (upgradeFilters.locationIds.includes('ALL')) {
      idsToSend = [];
      isAllSelectedToSend = false;
    } else if (configuration.locations.length === ids.length) {
      idsToSend = [...configuration.locations.map(l => l.id), 'ALL'] as unknown as number[];
      isAllSelectedToSend = true;
    } else {
      idsToSend = ids;
      isAllSelectedToSend = false;
    }

    setUpgradeFilters({isAllLocationsSelected: isAllSelectedToSend, locationIds: idsToSend});
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    sendUsageRequestWithBody(`${apiConfig.statisticsUsageService.hrReportFilterLocations}`, idsToSend.filter(i => i !== 'ALL'));
    setIsChangedManually(true);
  };

  const onShowB2BClick = (value: boolean) => {
    setUpgradeFilters({includeB2B: value});
    setIsChangedManually(true);
    setActivePage(1);  
  };

  const onShowPendingRequestsClick = (value: boolean) => {
    setUpgradeFilters({includeOnlyPendingAbsence: value});
    setIsChangedManually(true);
    setActivePage(1);
  };
    
  const locationsWithAllItem = [{  id: 'ALL',
    name: 'All locations',
    selected: false}, ...configuration.locations];

  const actionsRight = () => {
    return <>
      <div className='org-reports-actions__actions-endbox'>
        <TreeSelect
          fieldNames={{label: 'name', value: 'id'}}
          onChange={onSelectDivision}
          showCheckedStrategy={'SHOW_PARENT'}
          popupClassName={'divisionSelectPopup'}
          maxTagPlaceholder={() =>
            <div title={upgradeFilters.divisionIds.length === 1 ? divisionTagPlaceholder : ''}>
              {shortenStr(divisionTagPlaceholder, 15)}
            </div>
          }
          treeData={configuration.divisionTreeForHrReportFilterDtos as unknown as TreeSelectNodeType[]}
          placeholder="Select divisions"
          value={upgradeFilters.divisionIds}/>
        <TreeSelect
          fieldNames={{label: 'name', value: 'id'}}
          onChange={onSelectLocation}
          popupClassName={'locationSelectPopup'}
          maxTagPlaceholder={() =>
            <div title={locationTagPlaceholder}>
              {shortenStr(locationTagPlaceholder, 13)}
            </div>
          }
          treeData={locationsWithAllItem as unknown as TreeSelectNodeType[]}
          placeholder="Select locations"
          value={upgradeFilters.locationIds}
        />
        <ArrowButtons onLeftClick={prevMonth} onRightClick={nextMonth}/>
        <Picker
          className={'repDatePicker'}
          picker='month'
          onChange={value => setCurrentDate(value ? new Date(value._d) : todayDate)}
          format={'MMM, yyyy'}
          value={currentDate.toString()}
        />
        <Button style={{justifySelf: 'end'}} theme={{variant: 'secondary', color: 'theme'}} onClick={() => downloadAllData()} disabled={isDisabled} icon={isDisabled ? <Spinner className={'spinner'}/> : <DownloadIcon className="svg" />} >
          {t('downloadAll')}
        </Button>
      </div>
      
    </>;
  };

  return (
    <Layout header={{ layoutName: <BackArrow text={'orgReportTitle'} reportType={'Org'} /> }} className='org-reports'>
      <div className='org-reports-actions'>
        <AntRow gutter={[12, 4]}>
          <AntCol md={24} lg={6} xl={10} xxl={14}>
            <div >
              <SearchInput className={'search_input'}  debounce={1000} allowClear placeholder={t('typeToSearch')} prefix={<SearchIcon />}
                onChange={(e) => {
                  setSearchLine(e);
                  setActivePage(1);
                }} />
            </div>
          </AntCol>
          <AntCol md={24} lg={18} xl={14}  xxl={10}>{actionsRight()}</AntCol>
        </AntRow>
        <AntRow gutter={[12, 4]}>
          <AntCol span={24}>
            <div className='org-reports-actions__actions-startbox'>
              <div >
                <Checkbox checked={upgradeFilters.includeB2B} label={'Show B2B employees'} setChecked={onShowB2BClick}/>
              </div>
              <div>
                <Checkbox checked={upgradeFilters.includeOnlyPendingAbsence} label={'Filter by pending requests'} setChecked={onShowPendingRequestsClick}/>
              </div>
            </div>
          </AntCol>
        </AntRow>      
      </div>
      <InvalidReportAlert users={hrReportData.firstNameAndLastNameOfInvalidUsers} />
      <Table
        className={'report_org_table'}
        loading={{indicator: <Spinner className={'spinner'}/>, spinning: fetchingData}}
        dataSource={hrReportData.employeesDtoList}
        onRow={(record,index) => {
          return {
            onClick: () => {
              if(index !== undefined){
                gaEventTracker( 'from_reports_to_profile', 'org_report');
                setActivePage(activePage);
              }
            }
          };
        }}
        rowClassName={(record:HrReportEmployeeDto) => {
          return record.hasPendingRequests ? 'has-pending-requests' : '';
        }}      
        pagination={false}
        columns={columnsWithNessesaryType()}
      />
      <CustomPagination {...{
        total: hrReportData.total,
        defaultCurrent: activePage,
        current: activePage,
        position: ['bottomLeft'],
        showSizeChanger: true,
        pageSizeOptions: ['15', '30', '50', '100'],
        onShowSizeChange: (page, size) => {
          setShowPerPage(size);
          setPerPageLocalStorage(size);
          setActivePage(page);
        },
        pageSize: showPerPage,
        onChange: (e) => {
          setActivePage(e);
        },
        hideOnSinglePage: hrReportData.total <= 15
      }}/>
    </Layout>
  );
});

export default ReportDetailOrg;
